import { useId, useState } from 'react';
import { CloudOff } from 'lucide-react';
import type { FocusView } from '@/lib/focus/focusMachine';
import { LessonPicker } from './LessonPicker';
import { PanelSection } from './PanelSection';
import { SessionPlanPanel, type PlanChoice } from './SessionPlanPanel';
import { ViewPicker } from './ViewPicker';
import { BlockRecap } from './BlockRecap';
import { BreakActivities, type BreakActivity } from './BreakActivities';
import { ParkingLot } from './ParkingLot';
import {
  blockHeadline,
  blockSummary,
  type FocusLessonOption,
  type FocusSyncWaiting,
  type FocusToday,
  type FocusViewModel,
} from './focusViewModel';

function SyncNote({ pending, reason }: FocusSyncWaiting) {
  const what = pending === 1 ? '1 block is' : `${pending} blocks are`;
  return (
    <p className='flex items-start gap-2 text-sm text-muted-foreground'>
      <CloudOff className='mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
      <span>
        {what} saved on this device.{' '}
        {reason === 'offline'
          ? "They'll sync to your account when you're back online."
          : "We'll keep trying to sync them to your account."}
      </span>
    </p>
  );
}

function TodayLine({ blocks, minutes, waiting }: FocusToday) {
  return (
    <div className='space-y-2'>
      <p className='text-sm text-muted-foreground'>
        {blocks === 0
          ? 'No blocks yet today. Even one counts.'
          : `Today you've focused ${minutes} min across ${blocks} ${blocks === 1 ? 'block' : 'blocks'}.`}
      </p>
      {waiting && <SyncNote {...waiting} />}
    </div>
  );
}

// ── Set-up (idle) ───────────────────────────────────────────────────────

export interface FocusSetupPanelProps {
  intention: string;
  lessonId?: string;
  onIntentionChange: (text: string, lessonId?: string) => void;
  lessons: FocusLessonOption[];
  plan: PlanChoice;
  onPlanChange: (patch: Partial<PlanChoice>) => void;
  view: FocusView;
  onViewChange: (view: FocusView) => void;
  today: FocusToday;
  /** For the plan's end time. */
  now: number;
  night: boolean;
  /** Stories open the lesson list to show it at rest. */
  lessonsOpen?: boolean;
}

/** What the session is for, how to see it, and its plan. */
export function FocusSetupPanel(props: FocusSetupPanelProps) {
  const inputId = useId();
  const linked = props.lessons.find(l => l.id === props.lessonId);
  return (
    <div className='flex h-full flex-col gap-6'>
      <div className='space-y-2'>
        <label
          htmlFor={inputId}
          className='block text-sm font-semibold text-foreground'
        >
          {"What's this session for?"}
        </label>
        <input
          id={inputId}
          value={props.intention}
          onChange={e =>
            props.onIntentionChange(
              e.target.value,
              linked && e.target.value === linked.title ? linked.id : undefined
            )
          }
          placeholder='Finish the chapter 3 notes'
          maxLength={80}
          className='h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:text-base'
        />
        <LessonPicker
          lessons={props.lessons}
          selectedId={props.lessonId}
          defaultOpen={props.lessonsOpen}
          onPick={lesson => props.onIntentionChange(lesson.title, lesson.id)}
          onUnlink={() =>
            props.onIntentionChange(
              linked && props.intention === linked.title ? '' : props.intention
            )
          }
          onUseText={text => props.onIntentionChange(text)}
        />
      </div>
      <ViewPicker
        value={props.view}
        onChange={props.onViewChange}
        flow={props.plan.mode === 'flow'}
        night={props.night}
      />
      <SessionPlanPanel
        plan={props.plan}
        onChange={props.onPlanChange}
        now={props.now}
      />
      <div className='mt-auto'>
        <TodayLine {...props.today} />
      </div>
    </div>
  );
}

// ── Running ─────────────────────────────────────────────────────────────

export interface FocusRunPanelProps {
  view: FocusViewModel;
  parkedCount: number;
  onAddNote: (text: string) => void;
  onRemoveNote: (id: string) => void;
}

/**
 * Beside a running block, only the parking lot: the intention and the end
 * time sit under the clock, where the eye already is.
 */
export function FocusRunPanel(props: FocusRunPanelProps) {
  const parked = props.parkedCount;
  return (
    <div className='space-y-2'>
      <h2 className='flex items-baseline justify-between text-sm font-semibold text-foreground'>
        Parking lot
        {parked > 0 && (
          <span className='font-mono text-xs font-normal text-muted-foreground'>
            {parked} parked this session
          </span>
        )}
      </h2>
      <ParkingLot
        notes={props.view.parkingLot}
        onAdd={props.onAddNote}
        onRemove={props.onRemoveNote}
        placeholder='A stray thought? Park it and keep going…'
      />
    </div>
  );
}

// ── Break ───────────────────────────────────────────────────────────────

export interface FocusBreakPanelProps {
  view: FocusViewModel;
  onSaveRecap: (text: string) => void;
  onDismissRecap: () => void;
  defaultActivity?: BreakActivity;
}

/**
 * The break, on the page rather than in a sheet: the student is already
 * here, so nothing needs to open on top of anything. It asks for one thing
 * at a time - the optional recap, and once that is saved or skipped,
 * something restful to do.
 */
export function FocusBreakPanel(props: FocusBreakPanelProps) {
  const block = props.view.lastBlock;
  const [activity, setActivity] = useState<BreakActivity>(
    props.defaultActivity ?? 'breathe'
  );
  const long = props.view.breakKind === 'long';
  // One thing at a time here too: the recap first, then something restful.
  const recapPending = block !== null && !block.recap && !block.recapDismissed;

  return (
    <div className='space-y-6'>
      {block && (
        <div className='space-y-3'>
          <div>
            <h2 className='text-lg font-semibold text-foreground'>
              {blockHeadline(block)}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {blockSummary(block)}
            </p>
            {block.awayMinutes > 0 && (
              <p className='mt-1 text-xs text-muted-foreground'>
                Your block ended {block.awayMinutes} min ago, while you were
                away.
              </p>
            )}
          </div>
          <BlockRecap
            block={block}
            onSave={props.onSaveRecap}
            onDismiss={props.onDismissRecap}
          />
        </div>
      )}
      {!recapPending && (
        <PanelSection title={long ? 'A proper rest' : 'Use the break well'}>
          <BreakActivities activity={activity} onActivityChange={setActivity} />
        </PanelSection>
      )}
    </div>
  );
}
